import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  BrowserRouter,
  useRoutes,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Layout as AntLayout,
  Result,
  Row,
  Spin,
} from 'antd';
import { setDefaultOptions, loadCss, loadScript } from 'esri-loader';
import 'antd/dist/antd.min.css';
import 'app.scss';
import './assets/navojoa/fonts/index.scss';

import Layout from 'components/Layout';
import NotFound from 'components/NotFound';
import { getAuthorizedRoutes } from 'routes';
import { selectEntidad, selectIsAuthenticated, selectUser } from 'store/auth';
import { selectIsNetpayInitialized, setIsNetpayInitialized } from 'store/app';
import {
  getCarruseles,
  getContentypesPadrones,
  getEntidadConfig,
  getLadas,
  getNoticias,
  getTiposDeNoticias,
} from 'api/catalogos';
import { getInfoUser } from 'api/auth';
import { getPadrones } from 'api/padrones';
import { getClasificadoresDeTiposDeCargo } from 'api/recaudacion';
import {
  DEV,
  NETPAY_PUBLIC_KEY,
  PROD,
} from 'utils/env';
import { normalizeValue } from 'utils/formatters';
import { getNetPayCheckoutButton } from 'views/Payments/components/NetPayButton';
import useNetwork from 'hooks/useNetwork';
import isWebView from 'utils/isWebView';
import _logger from 'utils/logger';
import { COLORS } from 'components';

try {
  const version = '4.25';
  setDefaultOptions({ css: true });
  loadCss(version);
  loadScript({ version });
} catch (error) {
  _logger(error);
}

const StyledLayout = styled(AntLayout)`
  @media print {
    &, &* {
      height: auto !important;
      overflow: visible !important;
    }
  }
`;

const WebViewLayout = () => (
  <StyledLayout>
    <StyledLayout.Content style={{ height: '100vh', overflowY: 'scroll' }}>
      <Outlet />
    </StyledLayout.Content>
  </StyledLayout>
);

const Routes = ({ isAuthenticated, user }) => {
  const routes = useMemo(() => [
    {
      path: '/',
      element: isWebView() ? <WebViewLayout /> : <Layout />,
      children: getAuthorizedRoutes(isAuthenticated),
    },
    {
      path: 'not-found',
      element: <NotFound />,
    },
    {
      path: '*',
      element: DEV ? <NotFound /> : <Navigate to="/not-found" />,
    },
  ], [isAuthenticated, user]);
  return useRoutes(routes);
};

const App = () => {
  const dispatch = useDispatch();
  const network = useNetwork();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const netpayInitialized = useSelector(selectIsNetpayInitialized);
  const user = useSelector(selectUser);
  const entidad = useSelector(selectEntidad);
  const [fetchStatus, setFetchStatus] = useState('loading');

  const fetchRequirements = useCallback(async () => {
    const promises = [
      getEntidadConfig(dispatch),
      getPadrones(dispatch),
      getContentypesPadrones(dispatch),
      getLadas(dispatch),
      getClasificadoresDeTiposDeCargo(dispatch),
    ];

    if (isAuthenticated) {
      promises.push(getInfoUser(dispatch, user));
    }

    const allFetched = (await Promise.all(promises)).every(Boolean);
    setFetchStatus(allFetched || DEV ? 'success' : 'failed');
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    const getRequirements = async () => {
      const promises = [
        getEntidadConfig(dispatch),
        getPadrones(dispatch),
        getContentypesPadrones(dispatch),
        getLadas(dispatch),
        getClasificadoresDeTiposDeCargo(dispatch),
        getTiposDeNoticias(dispatch),
        getNoticias({ publicar_en_portada: true }, dispatch),
        getCarruseles(dispatch),
      ];
      if (isAuthenticated) {
        promises.push(getInfoUser(dispatch, user));
      }
      const _fetched = (await Promise.all(promises)).every((r) => r);
      if (DEV || _fetched) {
        setFetchStatus('success');
        return;
      }
      setFetchStatus('failed');
    };
    getRequirements();
  }, []);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const setupNetpay = useCallback(() => {
    if (fetchStatus !== 'success' || netpayInitialized || !window.NetPay || !NETPAY_PUBLIC_KEY) return;

    const button = getNetPayCheckoutButton();

    let { direccion } = entidad;

    if (!direccion) {
      direccion = {
        calle_principal: 'Portal Octaviano Muñoz Ledo S/N',
        codigo_postal: {
          d_codigo: '36000',
          municipio: {
            nom_mun: 'Salamanca',
            estado: {
              nombre_de_AGEE: 'Guanajuato',
            },
          },
        },
      };
    }

    const municipio = direccion?.codigo_postal?.municipio;

    Object.assign(button.dataset, {
      street1: normalizeValue(direccion?.calle_principal, false),
      country: 'Mexico',
      city: normalizeValue(municipio?.nom_mun, false),
      postalCode: normalizeValue(direccion?.codigo_postal?.d_codigo, false),
      state: normalizeValue(municipio?.estado?.nombre_de_AGEE, false),
    });

    window.NetPay.init(NETPAY_PUBLIC_KEY);
    window.NetPay.setSandboxMode(!PROD);
    dispatch(setIsNetpayInitialized());
  }, [fetchStatus, netpayInitialized, entidad, dispatch]);

  useEffect(() => {
    setupNetpay();
  }, [setupNetpay]);

  if (['loading', 'failed'].includes(fetchStatus)) {
    return (
      <Row style={{ width: '100vw', height: '100vh' }} align="middle" justify="center">
        {fetchStatus === 'loading' && <Spin spinning style={{ color: COLORS.accent }} />}
        {fetchStatus === 'failed' && (
          <Result
            status="500"
            title={network.online ? '' : 'Verifique su conexión'}
          />
        )}
      </Row>
    );
  }

  return (
    <BrowserRouter>
      <Routes isAuthenticated={isAuthenticated} user={user} />
    </BrowserRouter>
  );
};

export default App;
